import { create } from 'zustand';
import type { LabelKey } from '@/i18n/labels';
import { useAuthStore } from '@/stores/authStore';

/**
 * A single live-activity entry for the My Day feed. Derived from real
 * conversation-store transitions (which are themselves driven by hub events) —
 * never fabricated. `textKey` + `name` render as an interpolated line.
 */
export interface ActivityItem {
  id: string;
  icon: string;
  color: string;
  textKey: LabelKey;
  name: string;
  /**
   * The conversation the line is about. The My Day feed hides a line whose conversation has since been hidden from this
   * viewer (withdrawn, a 404, spam), so a withdrawn conversation's name is never shown. Optional: a line about nothing
   * in particular carries none.
   */
  sessionId?: string;
  /** ISO timestamp for relative-time display. */
  at: string;
}

const MAX_ITEMS = 25;

interface ActivityState {
  items: ActivityItem[];
  push: (item: ActivityItem) => void;
  clear: () => void;
}

export const useActivityStore = create<ActivityState>((set) => ({
  items: [],
  push: (item) =>
    set((state) => {
      // De-dup exact repeats (same id) and cap the list.
      if (state.items.some((i) => i.id === item.id)) return state;
      return { items: [item, ...state.items].slice(0, MAX_ITEMS) };
    }),
  clear: () => set({ items: [] }),
}));

/**
 * ADR-033 - the feed is a per tenant+agent cache like every other one: its lines name the customers and conversations
 * of whoever was signed in. The signed-in user going away (logout, an expired session) or changing to a different tenant
 * or agent empties it, so one person's activity (and the deep links on it) can never show to the next person on the same
 * browser. Signing IN from nothing wipes nothing (there is nothing of anyone else's), exactly like conversationStore.
 */
const scopeOf = (user: { tenantId?: string; agentId?: string } | null | undefined): string | null =>
  user ? `${user.tenantId ?? ''}|${user.agentId ?? ''}` : null;

let boundScope = scopeOf(useAuthStore.getState().user);
useAuthStore.subscribe((auth) => {
  const next = scopeOf(auth.user);
  if (next === boundScope) return;
  const hadScope = boundScope !== null;
  boundScope = next;
  if (hadScope) useActivityStore.getState().clear();
});
